<template>
    <RPBaseCard
        class="FormCard"
        :status="status"
        :title="title"
        :subtitle="subtitle"
        :open="isOpen"
    >
        <template v-slot:leadingIcon>
            <slot name="leadingIcon" />
        </template>

        <template v-slot:title>
            <slot name="title">
            </slot>
        </template>

        <template v-slot:subtitle>
            <slot name="subtitle">
            </slot>
        </template>

        <template v-slot:default>
            <slot
                name="actionButton"
                :open="isOpen"
                :toggleOpen="toggleOpen"
            >
                <RPButton
                    type="tertiary"
                    size="sm"
                    :status="status"
                    :label="buttonText"
                    :icon="buttonIcon"
                    @click="toggleOpen"
                />
            </slot>
        </template>

        <template v-slot:expanded>
            <div class="FormCard__form-container">
                <slot name="expanded">
                    <div class="FormCard__form">
                        <slot name="form" />
                    </div>
                    <div
                        v-if="showSubmit"
                        class="FormCard__button-container"
                    >
                        <RPButton
                            :disabled="submitDisabled"
                            class="FormCard__button"
                            label="Save"
                            @click.stop="submitForm"
                        />
                        <RPButton
                            class="FormCard__button"
                            type="tertiary"
                            label="Cancel"
                            @click="toggleOpen"
                        />
                    </div>
                </slot>
            </div>
        </template>
    </RPBaseCard>
</template>
<script>
import {Pencil} from "@reviewpush/streamline-regular/lib/interface-essential/Edit";
import {Remove as RemoveIcon}  from "@reviewpush/streamline-regular/lib/interface-essential/RemoveAdd";
import RPBaseCard from './RPBaseCard.vue';
import RPButton from 'components/Basic/Buttons/RPButton.vue';
import stat from 'status/index';

export default {
    name: 'RPFormCard',
    components: {
        RPBaseCard,
        RPButton,
    },
    props: {
        title: {
            type: String,
            default: '',
        },
        subtitle: {
            type: String,
            default: '',
        },
        cardButtonIcon: {
            type: [Object, Array],
            default: () => {
                return Pencil;
            }
        },
        showSubmit: {
            type: Boolean,
            default: true,
        },
        closeOnSubmit: {
            type: Boolean,
            default: true,
        },
        closeButtonText: {
            type: String,
            default: 'Cancel',
        },
        cardButtonText: {
            type: String,
            default: 'Edit',
        },
        disableSubmitButton: {
            type: Boolean,
            default: false,
        },
        status: {
            type: String,
            default: stat.STANDBY,
        },
    },
    data(){
        return {
            isOpen: false,
        };
    },
    computed: {
        isSaving(){
            return this.status === stat.SAVING;
        },
        buttonText(){
            return this.isOpen
                ? this.closeButtonText
                : this.cardButtonText;
        },
        buttonIcon() {
            return this.isOpen
                ? RemoveIcon
                : this.cardButtonIcon;
        },
        submitDisabled(){
            return this.disableSubmitButton
                || this.isSaving;
        }
    },
    watch: {
        status(newStat, oldStat){
            if(newStat !== oldStat){
                switch(newStat){
                case stat.SUCCESS:{
                    if(this.closeOnSubmit) {
                        this.$emit('form-close');
                        this.isOpen = false;
                    }
                    break;
                }

                case stat.ERROR:
                case stat.WARNING:{
                    if(this.closeOnSubmit){
                        this.isOpen = true;
                    }
                    break;
                }
                case stat.STANDBY:
                case stat.SAVING:
                default:
                    break;
                }
            }
        },
    },
    mounted(){
        document.addEventListener('click', this.close);
    },
    destroyed() {
        document.removeEventListener('click', this.close);
    },
    methods: {
        close(e){
            if(!this.$el.contains(e.target) && this.isOpen) {
                this.toggleOpen();
            }
        },
        toggleOpen(){
            this.$emit('clear-status');
            this.$emit('toggle-open', this.isOpen);
            this.isOpen = !this.isOpen;
            if(this.isOpen){
                document.addEventListener('keypress', this.maybeSubmitForm);
            } else {
                this.$emit('form-close');
                document.removeEventListener('keypress', this.maybeSubmitForm);
            }
        },
        maybeSubmitForm(event) {
            if (event.key === 'Enter' && this.isOpen) {
                this.submitForm();
            }
        },
        submitForm(){
            if(!this.submitDisabled){
                this.$emit('form-submit', this.changed);
            }
        },
    }
};
</script>
<style lang="scss">
    .FormCard {
        &__form {
            margin-bottom: 1rem;
        }
        &__button-container {
            display: flex;
        }
        &__button {
            margin-right: 1rem;
            min-width: 5.5rem;
        }
    }
</style>

<docs>
```[import](./RPFormCardExample.vue)
```
</docs>
